import { ExportOutlined } from "@ant-design/icons";
import { TailscaleSecretVault } from "@p0security/integrations-tailscale-shared/components";
import {
  VAULT_OUTSIDE_CONNECTOR_CLOUD,
  renderHosting,
} from "@p0security/integrations-tailscale-shared/hosting";
import { TAILSCALE_SCOPES } from "@p0security/integrations-tailscale-shared/scopes";
import type {
  TailscaleConnectorHosting,
  TailscaleCredentialConfig,
  TailscaleTailnetConfig,
} from "@p0security/integrations-tailscale-shared/types";
import { shellLines } from "@p0security/integrations-vaulted-credential-shared";
import {
  Alert,
  Button,
  CommandDisplay,
  Flex,
  Steps,
  Typography,
} from "@p0security/ui";
import { assertNever } from "@p0security/util/assertNever";
import type { ComponentProps, ReactNode } from "react";

import type { ReadGrantBlock } from "./tailnet-iac.ts";

// The secret-storing steps copy Auth0's (app#7529 @ 3c8d887b9a) instead of
// sharing them, so adding Tailscale changes no other integration.

const { Paragraph, Text } = Typography;

type OAuthClient = Extract<TailscaleCredentialConfig, { type: "oauth-client" }>;
type Vault = OAuthClient["vault"];

/** What a tailnet's page can show of its credential's Terraform. */
export type CredentialTerraform =
  /** A federated identity's Terraform */
  | {
      hosting: TailscaleConnectorHosting;
      status: "federated";
      terraform: string;
    }
  | {
      status: "ready";
      /** The Terraform that lets the connector read the OAuth client's
       * secret, in the order to apply it */
      blocks: ReadGrantBlock[];
      hosting: TailscaleConnectorHosting;
      /** The AWS account that holds the secret, when it isn't the
       * connector's */
      vaultAccountId: string | undefined;
    }
  /** The secret isn't in the connector's cloud, so no Terraform grants it */
  | { hosting: TailscaleConnectorHosting; status: "vault-outside-cloud" }
  /** The tailnet's connector isn't installed */
  | { status: "no-connector" };

type Step = { content: ReactNode; status?: "error" | "finish"; title: string };

const TAILSCALE_TRUST_CREDENTIALS_URL =
  "https://login.tailscale.com/admin/settings/trust-credentials";

/** The customer-managed KMS key for a secret in another account than the
 * connector's, in the command that creates the secret. */
const KMS_KEY_ARN = "<kms-key-arn>";

// Reads the client secret without echoing it or leaving it in the shell's
// history. Each command below takes it from standard input, so it's never an
// argument, which other users of the machine could list.
const READ_SECRET = "read -rs TAILSCALE_CLIENT_SECRET";

const CLEAR_SECRET = "unset TAILSCALE_CLIENT_SECRET";

const pipeSecret = (...lines: string[]) =>
  `printf %s "$TAILSCALE_CLIENT_SECRET" | ${shellLines(...lines)}`;

const toVaultLabel = (vault: Vault) =>
  TailscaleSecretVault.options[vault.type].label;

/** How to store the client secret: where the secret is, the command that
 * creates it, and the command for a secret that already exists. */
const toStoreInstructions = (
  { clientSecretName: name, vault }: OAuthClient,
  { crossAccount }: { crossAccount: boolean }
): { create: string; location: string; update: string } => {
  switch (vault.type) {
    case "aws-sm":
      return {
        create: pipeSecret(
          "aws secretsmanager create-secret",
          `--name ${name}`,
          ...(crossAccount ? [`--kms-key-id ${KMS_KEY_ARN}`] : []),
          "--secret-string file:///dev/stdin",
          `--region ${vault.secretsRegion}`
        ),
        location: `account ${vault.install} and region ${vault.secretsRegion}`,
        update: pipeSecret(
          "aws secretsmanager put-secret-value",
          `--secret-id ${name}`,
          "--secret-string file:///dev/stdin",
          `--region ${vault.secretsRegion}`
        ),
      };
    case "gcp-sm":
      return {
        create: pipeSecret(
          `gcloud secrets create ${name}`,
          `--project ${vault.install}`,
          "--data-file=-"
        ),
        location: `project ${vault.install}`,
        update: pipeSecret(
          `gcloud secrets versions add ${name}`,
          `--project ${vault.install}`,
          "--data-file=-"
        ),
      };
    default:
      throw assertNever(vault);
  }
};

/** The scopes a tailnet's credential needs, to copy. */
export const ScopeList: React.FC = () => (
  <CommandDisplay commands={TAILSCALE_SCOPES.join("\n")} />
);

const TrustCredentialsButton: React.FC = () => (
  <div>
    <Button
      href={TAILSCALE_TRUST_CREDENTIALS_URL}
      icon={<ExportOutlined />}
      rel="noopener noreferrer"
      target="_blank"
    >
      Open Tailscale&apos;s trust credentials
    </Button>
  </div>
);

const CreateFederatedIdentity: React.FC<{ clientId: string }> = ({
  clientId,
}) => (
  <Flex gap={8} vertical>
    <Text>
      The federated identity trusts the connector&apos;s own cloud identity, so
      no secret is stored anywhere. The Terraform below creates it with
      Tailscale&apos;s provider, or create it under Settings, Trust credentials,
      with the issuer and subject the Terraform names, its audience left unset,
      and exactly these scopes:
    </Text>
    <ScopeList />
    <Text>
      Its client ID must be the one you entered, <Text code>{clientId}</Text>.
    </Text>
    <TrustCredentialsButton />
  </Flex>
);

const CreateOAuthClient: React.FC<{ clientId: string }> = ({ clientId }) => (
  <Flex gap={8} vertical>
    <Text>
      In Tailscale&apos;s admin console, under Settings, Trust credentials,
      create an OAuth client with exactly these scopes, and no tags:
    </Text>
    <ScopeList />
    <Text>
      Its client ID must be the one you entered, <Text code>{clientId}</Text>.
      Copy its secret: Tailscale shows it once.
    </Text>
    <TrustCredentialsButton />
  </Flex>
);

const StoreSecret: React.FC<{
  credential: OAuthClient;
  crossAccount: boolean;
}> = ({ credential, crossAccount }) => {
  const { clientSecretName: name, vault } = credential;
  const { create, location, update } = toStoreInstructions(credential, {
    crossAccount,
  });
  return (
    <Flex gap={8} vertical>
      <Text>
        Store the OAuth client&apos;s secret in {toVaultLabel(vault)} as the
        secret <Text code>{name}</Text>, in {location}.{" "}
        {crossAccount && (
          <>
            The connector runs in another AWS account, so the secret needs a
            customer-managed KMS key: replace <Text code>{KMS_KEY_ARN}</Text>{" "}
            with that key&apos;s ARN.{" "}
          </>
        )}
        Run this first, paste the secret and press Enter. It reads the secret
        without showing it:
      </Text>
      <CommandDisplay commands={READ_SECRET} />
      <Text>Then create the secret:</Text>
      <CommandDisplay commands={create} />
      <Text>
        If the secret already exists, store the client secret as a new version
        instead:
      </Text>
      <CommandDisplay commands={update} />
      <Text>Then clear the client secret from your shell:</Text>
      <CommandDisplay commands={CLEAR_SECRET} />
      <Text>
        P0 never reads the client secret, and it stays out of Terraform&apos;s
        state. Only your connector reads it.
      </Text>
    </Flex>
  );
};

/** An example of what a tailnet's policy file adds for one P0 posture: the
 * posture, whose assertion reads the attribute P0 sets, and a grant gated on
 * it. */
const POSTURE_EXAMPLE = `"postures": {
  "posture:p0_prod_db": {"assertions": ["custom:p0_prod_db == true"]},
},
"grants": [
  {
    "src": ["autogroup:member"],
    "dst": ["tag:prod-db"],
    "ip": ["tcp:5432"],
    "srcPosture": ["posture:p0_prod_db"],
  },
],`;

/** How a tailnet's policy file gates grants on P0 postures. */
const PostureSnippet: React.FC = () => (
  <Flex gap={8} vertical>
    <Text>
      P0 never writes your policy file. Gate each grant P0 may unlock on a P0
      posture, <Text code>posture:p0_&lt;name&gt;</Text>, whose assertion is{" "}
      <Text code>custom:p0_&lt;name&gt; == true</Text>: P0 sets that attribute
      on the requestor&apos;s device until the request ends. For example:
    </Text>
    <CommandDisplay commands={POSTURE_EXAMPLE} />
    <Text>
      A posture name takes lowercase letters, digits and underscores. Several
      postures in one <Text code>srcPosture</Text> are any-of. A grant that
      names its own <Text code>srcPosture</Text> no longer gets your{" "}
      <Text code>defaultSrcPosture</Text>, so repeat any you rely on. Requestors
      then request each posture by its name.
    </Text>
  </Flex>
);

const toDeployedStep = (
  connectorId: string,
  hosting: TailscaleConnectorHosting
): Omit<Step, "title"> => ({
  content: (
    <Text>
      Done: the connector {connectorId} runs as {renderHosting(hosting)}.
    </Text>
  ),
  status: "finish",
});

const NoConnector: React.FC<{ connectorId: string }> = ({ connectorId }) => (
  <Alert
    message={`The connector ${connectorId} isn't installed. Install it again under Connector, with the same ID and hosting, or remove this tailnet and add it again with another connector.`}
    showIcon
    type="error"
  />
);

/** Says where to apply the Terraform that lets the connector read the client
 * secret. */
const GrantRead: React.FC<{
  vaultAccountId: string | undefined;
}> = ({ vaultAccountId }) => (
  <Text>
    Apply the Terraform below. It lets the connector read this one secret, and
    no other.
    {vaultAccountId && (
      <>
        {" "}
        The secret and the connector are in different AWS accounts, so there are
        two configurations: one for the connector&apos;s account, and one for
        account {vaultAccountId}, which holds the secret.
      </>
    )}
  </Text>
);

/** What "Next" checks, and that "Finish" completes the install. */
const Validate: React.FC<{ tailnet: string }> = ({ tailnet }) => (
  <Flex gap={8} vertical>
    <Text>
      Click &quot;Next&quot;. P0 asks the connector to authenticate to tailnet{" "}
      {tailnet}, check that the credential has every scope above, set and remove
      a test attribute on one device, and count the P0 postures in the policy
      file. If a check fails, P0 says which one.
    </Text>
    <Text>
      When the checks pass, click &quot;Finish&quot; on the next page. Access on{" "}
      {tailnet} can&apos;t be requested until you do.
    </Text>
  </Flex>
);

/** The steps that depend on the credential's Terraform, by its state. */
const toCredentialSteps = (
  item: TailscaleTailnetConfig,
  terraform: CredentialTerraform
): Step[] => {
  const { connector, credential } = item;
  switch (terraform.status) {
    case "no-connector":
      return [
        {
          content: <NoConnector connectorId={connector} />,
          status: "error",
          title: "Deploy the connector",
        },
      ];
    case "federated":
      return [
        {
          ...toDeployedStep(connector, terraform.hosting),
          title: "Deploy the connector",
        },
        {
          content: <CreateFederatedIdentity clientId={credential.clientId} />,
          title: "Create the federated identity",
        },
      ];
    case "ready":
    case "vault-outside-cloud": {
      if (credential.type !== "oauth-client") return [];
      const crossAccount =
        terraform.status === "ready" && terraform.vaultAccountId !== undefined;
      return [
        {
          content: <CreateOAuthClient clientId={credential.clientId} />,
          title: "Create the OAuth client",
        },
        {
          content: (
            <StoreSecret credential={credential} crossAccount={crossAccount} />
          ),
          title: `Store its secret in ${toVaultLabel(credential.vault)}`,
        },
        {
          ...toDeployedStep(connector, terraform.hosting),
          title: "Deploy the connector",
        },
        terraform.status === "ready"
          ? {
              content: <GrantRead vaultAccountId={terraform.vaultAccountId} />,
              title: "Let the connector read the secret",
            }
          : {
              content: (
                <Alert
                  message={`${VAULT_OUTSIDE_CONNECTOR_CLOUD} Remove this tailnet and add it again.`}
                  showIcon
                  type="error"
                />
              ),
              status: "error",
              title: "Let the connector read the secret",
            },
      ];
    }
    default:
      throw assertNever(terraform);
  }
};

/**
 * The steps that install one tailnet, in the order the customer takes them,
 * shown above the tailnet's Terraform.
 *
 * The customer deploys the connector before adding the tailnet, since the
 * tailnet's form lists only installed connectors, so that step shows as done,
 * or as failed if someone has since removed the connector.
 */
export const TailnetSteps: React.FC<{
  item: TailscaleTailnetConfig;
  tailnet: string;
  terraform: CredentialTerraform;
}> = ({ item, tailnet, terraform }) => {
  const steps: Step[] = [
    ...toCredentialSteps(item, terraform),
    { content: <PostureSnippet />, title: "Gate grants on P0 postures" },
    { content: <Validate tailnet={tailnet} />, title: "Validate" },
  ];
  const items: ComponentProps<typeof Steps>["items"] = steps.map(
    ({ content, status, title }) => ({
      description: content,
      status: status ?? "process",
      title,
    })
  );
  return <Steps direction="vertical" items={items} />;
};

/** What to do with a connector's Terraform, shown above it. */
export const ConnectorHelp: React.FC<{
  connectorId: string;
  hosting: TailscaleConnectorHosting;
}> = ({ connectorId, hosting }) => (
  <>
    <Paragraph>
      The Terraform below deploys the connector {connectorId} as{" "}
      {renderHosting(hosting)}, and lets P0 invoke it. The connector reaches
      Tailscale&apos;s API over the internet, so it needs no VPC.
      {hosting.type === "aws" &&
        " It also turns on the account's outbound identity federation, which lets the connector sign in to Tailscale with a federated identity and no secret."}
    </Paragraph>
    <Paragraph>
      Then click &quot;Next&quot;, and P0 checks that it can invoke the
      connector. When it can, click &quot;Finish&quot; on the next page:
      tailnets can&apos;t name the connector until you do.
    </Paragraph>
  </>
);
